"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Plus } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import { validateFields } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import type { FormProps, TabItem, RowItem, ColumnItem, ValueItem, Form6Config, DEFAULT_CONFIG } from "./types";
import { TabHeader, TabContent } from "./components";
import { useDataOperations } from "./hooks";

/**
 * Form_6 - Generic Configurable Table Form
 * 
 * A flexible form component that manages tabbed data with rows, columns, and values.
 * Can be configured for different use cases (Program Fee, Pricing Tables, etc.)
 * 
 * Configuration via variantDetails:
 * - tabsLabel: "Fee Types", "Categories", etc.
 * - rowsLabel: "Rows", "Categories"
 * - columnsLabel: "Columns", "Semesters"
 * - showTabStyling: Enable/disable tab color customization
 * - showRowStyling: Enable/disable row color customization
 * - showColumnStyling: Enable/disable column color customization
 * - showCellStyling: Enable/disable cell color customization
 */
const Form_6 = ({
  selectedSection,
  onSave,
  pageId,
  pageComponentId,
  variantDetails,
}: FormProps) => {
  // Merge config with defaults
  const config: Form6Config = useMemo(() => ({
    tabsLabel: variantDetails?.tabsLabel || "Tabs",
    tabsSingularLabel: variantDetails?.tabsSingularLabel || "Tab",
    rowsLabel: variantDetails?.rowsLabel || "Rows",
    rowsSingularLabel: variantDetails?.rowsSingularLabel || "Row",
    columnsLabel: variantDetails?.columnsLabel || "Columns",
    columnsSingularLabel: variantDetails?.columnsSingularLabel || "Column",
    categoryHeaderLabel: variantDetails?.categoryHeaderLabel || "Category",
    settingsIcon: variantDetails?.settingsIcon || "📝",
    tabsIcon: variantDetails?.tabsIcon || "📋",
    showTabStyling: variantDetails?.showTabStyling !== false,
    showRowStyling: variantDetails?.showRowStyling !== false,
    showColumnStyling: variantDetails?.showColumnStyling !== false,
    showCellStyling: variantDetails?.showCellStyling !== false,
    showCategoryHeader: variantDetails?.showCategoryHeader !== false,
    // Granular color controls
    showBorderColor: variantDetails?.showBorderColor !== false,
    showTextColor: variantDetails?.showTextColor !== false,
    showBgColor: variantDetails?.showBgColor !== false,
  }), [variantDetails]);

  // Parent component state
  const [componentFields, setComponentFields] = useState<Record<string, any>>({});
  const [componentFieldErrors, setComponentFieldErrors] = useState<Record<string, boolean>>({});
  const [componentFieldMessages, setComponentFieldMessages] = useState<Record<string, string>>({});
  const [parentId, setParentId] = useState<number | null>(null);

  // Data state
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [rows, setRows] = useState<RowItem[]>([]);
  const [columns, setColumns] = useState<ColumnItem[]>([]);
  const [values, setValues] = useState<ValueItem[]>([]);

  // UI state
  const [expandedTabId, setExpandedTabId] = useState<number | null>(null);
  
  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'tab' | 'row' | 'column';
    id: number;
    name?: string;
  } | null>(null);

  // Refs
  const prevPageComponentIdRef = useRef<string | undefined>(undefined);
  const prevComponentIdRef = useRef<string | undefined>(undefined);

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user?.user);

  // Get configurations from variant details
  const componentFieldsConfig = variantDetails?.componentFields || [];
  const getMethod = variantDetails?.getMethod;
  const createMethod = variantDetails?.createMethod;
  const updateMethod = variantDetails?.updateMethod;

  const reduxState = useAppSelector((state) =>
    variantDetails?.selector ? variantDetails.selector(state.page as any) : null
  ) as Record<string, any> | null;

  // ============================================================================
  // Data Loading
  // ============================================================================
  const loadData = useCallback(async () => {
    if (!getMethod || !pageComponentId) {
      initializeDefaults();
      return;
    }

    try {
      const response = await getMethod(Number(pageComponentId));
      const data = response?.data || response || {};

      if (data && Object.keys(data).length > 0) {
        const fields: Record<string, any> = {};
        componentFieldsConfig.forEach((field: any) => {
          fields[field.fieldName] = data[field.fieldName] ?? field.defaultValue ?? "";
        });
        setComponentFields(fields);
        setParentId(data.id || null);
        
        // Support both generic and specific field names
        const loadedTabs = data.tabs || data.feeTypes || [];
        const loadedRows = data.rows || data.categories || [];
        const loadedColumns = data.columns || data.semesters || [];
        const loadedValues = data.values || data.feeValues || [];
        
        setTabs(loadedTabs);
        setRows(loadedRows);
        setColumns(loadedColumns);
        setValues(loadedValues);

        if (variantDetails?.setAction) {
          // Normalize data to include both naming conventions for Redux/preview
          const normalizedData = {
            ...data,
            // Generic names
            tabs: loadedTabs,
            rows: loadedRows,
            columns: loadedColumns,
            values: loadedValues,
            // Specific names (for preview component)
            feeTypes: loadedTabs,
            categories: loadedRows,
            semesters: loadedColumns,
            feeValues: loadedValues,
          };
          dispatch(variantDetails.setAction(normalizedData));
        }
      } else {
        initializeDefaults();
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      initializeDefaults();
    }
  }, [getMethod, pageComponentId, componentFieldsConfig, variantDetails, dispatch]);

  const initializeDefaults = () => {
    const fields: Record<string, any> = {};
    componentFieldsConfig.forEach((field: any) => {
      fields[field.fieldName] = field.defaultValue ?? "";
    });
    setComponentFields(fields);
    setParentId(null);
    setTabs([]);
    setRows([]);
    setColumns([]);
    setValues([]);
  };

  useEffect(() => {
    if (prevPageComponentIdRef.current !== pageComponentId) {
      prevPageComponentIdRef.current = pageComponentId;
      loadData();
    }
  }, [pageComponentId, loadData]);

  // Refetch data when componentId changes OR when data object reference changes (e.g., after clone operation)
  useEffect(() => {
    const currentComponentId = selectedSection?.componentId;
    
    // Case 1: ComponentId changed (new widget or first clone into empty widget)
    const componentIdChanged = currentComponentId && currentComponentId !== prevComponentIdRef.current;
    
    // Case 2: ComponentId is same but data was updated (clone into existing widget)
    const dataWasUpdated = 
      currentComponentId && 
      currentComponentId === prevComponentIdRef.current &&
      selectedSection?.data &&
      pageComponentId;
    
    if (componentIdChanged || dataWasUpdated) {
      prevComponentIdRef.current = currentComponentId;
      // Reload all data including tabs, rows, columns, and values
      loadData();
    }
  }, [selectedSection?.componentId, selectedSection?.data, pageComponentId, loadData]);

  // Use the data operations hook
  const dataOps = useDataOperations({
    parentId,
    tabs,
    rows,
    columns,
    values,
    currentUserId: currentUser?.id,
    loadData,
    variantDetails,
    config,
  });

  // ============================================================================
  // Delete Handlers with Confirmation
  // ============================================================================
  const handleTabDelete = (tabId: number, tabName?: string) => {
    setDeleteTarget({ type: 'tab', id: tabId, name: tabName });
    setIsDeleteDialogOpen(true);
  };

  const handleRowDelete = (rowId: number, rowName?: string) => {
    setDeleteTarget({ type: 'row', id: rowId, name: rowName });
    setIsDeleteDialogOpen(true);
  };

  const handleColumnDelete = (colId: number, colName?: string) => {
    setDeleteTarget({ type: 'column', id: colId, name: colName });
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    let deleted = false;
    switch (deleteTarget.type) {
      case 'tab':
        deleted = await dataOps.handleDeleteTab(deleteTarget.id) || false;
        if (deleted && expandedTabId === deleteTarget.id) {
          setExpandedTabId(null);
        }
        break;
      case 'row':
        await dataOps.handleDeleteRow(deleteTarget.id);
        deleted = true;
        break;
      case 'column':
        await dataOps.handleDeleteColumn(deleteTarget.id);
        deleted = true;
        break;
    }

    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
    return deleted;
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getDeleteDialogProps = () => {
    if (!deleteTarget) return { title: '', description: '' };

    const typeLabels = {
      tab: config.tabsSingularLabel?.toLowerCase(),
      row: config.rowsSingularLabel?.toLowerCase(),
      column: config.columnsSingularLabel?.toLowerCase(),
    };

    const typeLabel = typeLabels[deleteTarget.type];
    
    return {
      title: `Delete ${typeLabel}`,
      itemName: deleteTarget.name,
      description: `Are you sure you want to delete this ${typeLabel} and all its data? This action cannot be undone.`,
    };
  };

  // ============================================================================
  // Component Save
  // ============================================================================
  const handleComponentSave = async () => {
    const { errors, messages } = validateFields(componentFieldsConfig, componentFields);
    setComponentFieldErrors(errors);
    setComponentFieldMessages(messages);

    if (Object.keys(errors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors",
        variant: "destructive",
      });
      return;
    }

    try {
      const isUpdate = Boolean(reduxState?.id);
      const payload = {
        ...componentFields,
        page_id: Number(pageId),
        variant: selectedSection.variant,
        ...(pageComponentId ? { component_id: Number(pageComponentId) } : {}),
        ...(isUpdate ? { updated_by: currentUser?.id } : { created_by: currentUser?.id }),
      };

      if (isUpdate && updateMethod && reduxState?.id) {
        await updateMethod(Number(reduxState.id), payload);
        toast({ title: "Success", description: "Component updated successfully" });
      } else if (createMethod) {
        const result = await createMethod(payload);
        const newId = result?.data?.id || result?.id;
        if (newId) setParentId(newId);
        toast({ title: "Success", description: "Component created successfully" });
      }

      await loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to save",
        variant: "destructive",
      });
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Component Settings */}
      <div className="mb-6 border-b pb-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <span>{config.settingsIcon}</span> Component Settings
        </h3>
        <FieldsFormatter
          data={componentFields}
          fields={componentFieldsConfig}
          updateParent={true}
          onChange={(data) => setComponentFields(data)}
          validationErrors={componentFieldErrors}
          validationMessages={componentFieldMessages}
          className="p-0 bg-transparent"
        />
        <div className="mt-4">
          <Button onClick={handleComponentSave} classType="admin">
            {reduxState?.id ? "Update Component" : "Save Component"}
          </Button>
        </div>
      </div>

      {/* Tabs Section */}
      {parentId && (
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <span>{config.tabsIcon}</span> {config.tabsLabel}
            </h3>
            <button
              onClick={dataOps.handleAddTab}
              className="flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add {config.tabsSingularLabel}
            </button>
          </div>

          {tabs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
              No {config.tabsLabel?.toLowerCase()} yet. Click "Add {config.tabsSingularLabel}" to create one.
            </div>
          ) : (
            <div className="space-y-4">
              {tabs.map((tab) => {
                const isExpanded = expandedTabId === tab.id;
                const tabRows = dataOps.getRowsForTab(tab.id);
                const tabCols = dataOps.getColumnsForTab(tab.id);

                return (
                  <div key={tab.id} className="border rounded-lg overflow-hidden">
                    {/* Tab Header */}
                    <TabHeader
                      tab={tab}
                      isExpanded={isExpanded}
                      rowCount={tabRows.length}
                      colCount={tabCols.length}
                      config={config}
                      onToggleExpand={() => setExpandedTabId(isExpanded ? null : tab.id)}
                      onUpdateStyle={dataOps.handleUpdateTabStyle}
                      onDelete={(tabId) => handleTabDelete(tabId, tab.name)}
                    />

                    {/* Tab Content */}
                    {isExpanded && (
                      <TabContent
                        tab={tab}
                        rows={tabRows}
                        columns={tabCols}
                        config={config}
                        getValue={dataOps.getValue}
                        onAddRow={dataOps.handleAddRow}
                        onAddColumn={dataOps.handleAddColumn}
                        onDeleteRow={(rowId) => {
                          const row = tabRows.find(r => r.id === rowId);
                          handleRowDelete(rowId, row?.name);
                        }}
                        onDeleteColumn={(colId) => {
                          const col = tabCols.find(c => c.id === colId);
                          handleColumnDelete(colId, col?.name);
                        }}
                        onUpdateRowName={dataOps.handleUpdateRowName}
                        onUpdateColumnName={dataOps.handleUpdateColumnName}
                        onUpdateCategoryHeader={dataOps.handleUpdateCategoryHeader}
                        onSaveCell={dataOps.handleSaveCell}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Show message if component not saved yet */}
      {!parentId && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
          Save the component settings first to manage {config.tabsLabel?.toLowerCase()}.
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title={getDeleteDialogProps().title}
          itemName={getDeleteDialogProps().itemName}
          description={getDeleteDialogProps().description}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default Form_6;

